/**
 * Dashboard Layout Component
 * Main layout for authenticated pages with sidebar and header
 */

import { Suspense, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';

import { Sidebar } from './Sidebar';
import { DashboardHeader } from './DashboardHeader';
import { HomeFooter } from '../home';
import { OfflineBanner } from '../common/NetworkStatusIndicator';
import LoadingSpinner from '../common/LoadingSpinner';

// Sidebar width constants
export const SIDEBAR_WIDTH = 280;

export function DashboardLayout() {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        width={SIDEBAR_WIDTH}
        isMobile={isMobile}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Without it the flex item grows to its widest child: a wide table
          // would stretch the whole page instead of scrolling in its container.
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          // On desktop: add margin when sidebar is open to push content
          marginLeft: {
            xs: 0,
            md: sidebarOpen ? `${SIDEBAR_WIDTH}px` : 0,
          },
          // Smooth transition
          transition: muiTheme.transitions.create('margin', {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Header */}
        <DashboardHeader
          onToggleSidebar={handleToggleSidebar}
          sidebarOpen={sidebarOpen}
        />

        {/* Offline banner - slides in when network is unavailable */}
        <OfflineBanner />

        {/* Page content */}
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            backgroundColor: (theme) => theme.palette.background.default,
            overflow: 'auto',
          }}
        >
          {/* Scope lazy-route loading to the content area only. This keeps the
              sidebar and header mounted during navigation, so the header's
              user menu (a MUI Portal) closes cleanly instead of being orphaned
              in the DOM when the page-level Suspense fallback would swap out
              the whole layout. */}
          <Suspense fallback={<LoadingSpinner fullScreen={false} />}>
            <Outlet />
          </Suspense>
        </Box>

        {/* Footer */}
        <HomeFooter />
      </Box>
    </Box>
  );
}

export default DashboardLayout;
